import { useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Redirect, Route, useLocation } from 'react-router';

import { Auth, useAuth } from '@zg-rentals/auth-comet';
import { authRedirects } from '@zg-rentals/auth-comet';

import { RMWContext } from '../../providers/RMWContext';
import { setUser } from '../../slices/user';

import StringConstants from '../../constants/StringConstants';

import type React from 'react';

// Note: useful as client-side redirects to login route should trigger SSR for rental-growth-web
const IsomorphicRedirect = ({ to }: { to: string }) => {
  if (typeof window !== 'undefined') {
    window.location.href = to;
    return null;
  } else {
    return <Redirect to={to} />;
  }
};

const AuthCheckWrapper = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useDispatch();

  const { pathname, search } = useLocation();

  const { isInMobileWebView, brand } = useContext(RMWContext);

  // Note: synchronizes user state with Redux on the client, ensuring Redux user and auth-comet user
  // are equivalent
  const { user } = useAuth();

  useEffect(() => {
    if (user) dispatch(setUser(user));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <Auth.Boundary
      onUnauthorized={() => (
        <Route
          render={({ staticContext }) => {
            if (staticContext) {
              staticContext.statusCode = 302;
            }

            // Note: safe to use Redirect on client here as this route does live in ZRMW
            if (__DEV__) {
              return <Redirect to={'/rental-manager/featureFlags'} />;
            }

            // If in mobile view, and a native login page is defined, route to that
            const manifest = authRedirects[brand as keyof typeof authRedirects] ?? authRedirects.zillow;
            if (isInMobileWebView && 'nativeAppSignInPage' in manifest) {
              const redirectTo = manifest.nativeAppSignInPage?.(pathname, search);
              if (redirectTo) {
                return <IsomorphicRedirect to={redirectTo} />;
              }
            }

            const params = new URLSearchParams(search);
            const isMfaRequired = user?.isInitialMultiFactorAuthenticationRequired;
            params.delete('mfaStepUp');

            // Otherwise just route to sign in page
            const loginUrl = manifest.signInPage(
              pathname,
              params.size ? `?${params.toString()}` : '',
              brand === StringConstants.UNFORMATTED_BRAND_NAMES.zillow ? `/rental-manager/login/` : undefined,
            );

            return (
              <IsomorphicRedirect
                to={isMfaRequired ? `${loginUrl}${loginUrl.includes('?') ? '&' : '?'}mfaStepUp=true` : loginUrl}
              />
            );
          }}
        />
      )}
    >
      {children}
    </Auth.Boundary>
  );
};

export default AuthCheckWrapper;
